import { NavLink, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/chi-sono", label: "Chi Sono" },
  { to: "/percorsi", label: "I miei percorsi" },
  { to: "/metodo", label: "Il Metodo" },
  { to: "/casistudio", label: "Casi Studio" },
  { to: "/contatti", label: "Contatti" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const loc = useLocation();
  const isFunnel = loc.pathname.startsWith("/checkup");

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Modalità funnel (traffico Meta Ads): solo logo, nessuna via di uscita
  if (isFunnel) {
    return (
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-40 transition-all ${
          scrolled ? "bg-[#F6F3EF]/85 backdrop-blur-md border-b border-[#323C36]/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center h-20">
          <span data-testid="logo-link" className="flex items-center cursor-default select-none">
            <img
              src="/assets/logo-primary.png"
              alt="Roberta Castagna — Business Consultant"
              className="h-12 sm:h-14 w-auto"
            />
          </span>
        </div>
      </header>
    );
  }

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all ${
        scrolled ? "bg-[#F6F3EF]/85 backdrop-blur-md border-b border-[#323C36]/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <img
            src="/assets/logo-primary.png"
            alt="Roberta Castagna — Business Consultant"
            className="h-12 sm:h-14 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to} to={l.to} end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) =>
                `text-sm tracking-wide link-underline ${isActive ? "text-[#323C36]" : "text-[#2A3229]/70 hover:text-[#323C36]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {user && user.id ? (
            <>
              <Link to="/checkup" data-testid="nav-quiz" className="inline-flex items-center gap-2 bg-[#323C36] hover:bg-[#1F2822] text-[#F6F3EF] px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-semibold transition-colors">
                Check‑Up
              </Link>
              <button data-testid="logout-btn" onClick={logout} className="text-xs uppercase tracking-widest text-[#2A3229]/60 hover:text-[#323C36]">
                Esci
              </button>
            </>
          ) : (
            <Link
              data-testid="nav-login"
              to="/checkup"
              onClick={() => {
                // Se siamo già su /checkup, forza il reset completo del quiz
                // (altrimenti React Router non rimonta e l'utente resta bloccato
                // nella schermata precedente o in stato di errore).
                if (loc.pathname === "/checkup") {
                  window.dispatchEvent(new CustomEvent("checkup:reset"));
                }
              }}
              className={`${loc.pathname === "/" ? "btn-accent" : "btn-ghost-accent"} !py-2.5 !px-5 text-[11px]`}
            >
              Check‑Up Aziendale
            </Link>
          )}
        </nav>

        <button data-testid="menu-toggle" className="lg:hidden text-[#2A3229]" onClick={() => setOpen(!open)} aria-label="menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F6F3EF] border-t border-[#323C36]/10">
          <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"}
                data-testid={`mnav-${l.label.toLowerCase()}`}
                className={({isActive}) => `text-base ${isActive ? "text-[#323C36]" : "text-[#2A3229]/80"}`}>
                {l.label}
              </NavLink>
            ))}
            {user && user.id ? (
              <>
                <NavLink to="/checkup" data-testid="mnav-quiz" className="text-base text-[#A8674F]">Check‑Up Aziendale</NavLink>
                <button data-testid="mlogout" onClick={logout} className="text-left text-sm uppercase tracking-widest text-[#2A3229]/60">Esci</button>
              </>
            ) : (
              <Link
                data-testid="mnav-login"
                to="/checkup"
                onClick={() => {
                  if (loc.pathname === "/checkup") {
                    window.dispatchEvent(new CustomEvent("checkup:reset"));
                  }
                }}
                className={`${loc.pathname === "/" ? "btn-accent" : "btn-ghost-accent"} !w-full`}
              >
                Accedi al Check‑Up
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
